import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ProgramScheduleV2, DeliveryModeV2, ScheduleBatchEntry } from '@/types/program-v2';
import { BulkDateModal } from '@/components/ui/bulk-date-modal';
import { parseBulkDateInput } from '@/utils/date-parser';
import { DatePicker } from '@/components/ui/date-picker';
import { CopyFromProgramType } from '@/components/programs/CopyFromProgramType';
import { toast } from 'sonner';

interface ScheduleStepV2Props {
  data: { schedule?: ProgramScheduleV2 };
  onUpdate: (data: { schedule?: ProgramScheduleV2 }) => void;
  programType?: string;
  onFieldChange?: (fieldPath: string) => void;
  activeProgramType?: DeliveryModeV2;
  programTypes?: Record<string, any>;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

// Max day for a month (non-leap); backend may validate leap year for Feb.
const getMaxDaysInMonth = (monthName: string): number => {
  const i = MONTH_NAMES.indexOf(monthName as any);
  if (i === -1) return 31;
  const days = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[i] ?? 31;
};

// Parse comma-separated days (1–31 or 1–28/29/30 per month) to sorted unique string array for API.
const parseDaysOfMonthStrings = (input: string, maxDay: number = 31): string[] => {
  const parts = input.split(/[\s,]+/).map((s) => parseInt(s.trim(), 10));
  const valid = [...new Set(parts.filter((n) => !Number.isNaN(n) && n >= 1 && n <= maxDay))].sort((a, b) => a - b);
  return valid.map((n) => String(n));
};

// Parse comma-separated days (1–31) to sorted unique number array for API.
const parseDaysOfMonthNumbers = (input: string, maxDay: number = 31): number[] => {
  const parts = input.split(/[\s,]+/).map((s) => parseInt(s.trim(), 10));
  const valid = [...new Set(parts.filter((n) => !Number.isNaN(n) && n >= 1 && n <= maxDay))].sort((a, b) => a - b);
  return valid;
};

// Summary text from scheduleBatches: "Jan 5, Jan 15, Feb 20"
const scheduleBatchesSummary = (entries: ScheduleBatchEntry[]): string => {
  if (!entries?.length) return '';
  const parts: string[] = [];
  const shortMonth = (m: string) => m.slice(0, 3);
  entries.forEach(({ month, dates }) => {
    const days = dates.filter(Boolean).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    days.forEach((d) => parts.push(`${shortMonth(month)} ${d}`));
  });
  return parts.join(', ');
};

// Backend V2 Simple Schedule - matches Zod schema
const getDefaultScheduleForType = (type: string): ProgramScheduleV2 => {
  switch (type) {
    case 'hybrid':
    case 'online':
      return { dates: [] };
    case 'on-campus':
    case 'via-zoom':
      return { batches: [] };
    default:
      return {};
  }
};

export const ScheduleStepV2: React.FC<ScheduleStepV2Props> = ({ 
  data, 
  onUpdate, 
  programType = 'hybrid', 
  onFieldChange,
  activeProgramType,
  programTypes,
}) => {
  // Normalize schedule from API: support snake_case and ensure scheduleBatches[].dates are string[]
  const rawSchedule = data?.schedule;
  const scheduleData: ProgramScheduleV2 = (() => {
    const base = rawSchedule || getDefaultScheduleForType(programType);
    const batches = base.scheduleBatches ?? (base as any).schedule_batches;
    // Important: do NOT coerce empty arrays into `scheduleBatches: []` — backend treats schedule modes as mutually exclusive.
    if (!Array.isArray(batches) || batches.length === 0) return base;
    const normalized: ScheduleBatchEntry[] = batches.map((entry: any) => ({
      month: entry.month || 'January',
      dates: Array.isArray(entry.dates) ? entry.dates.map((d: any) => String(d)) : [],
    }));
    return { ...base, scheduleBatches: normalized };
  })();

  const [bulkDateModal, setBulkDateModal] = useState<{
    isOpen: boolean;
    batchIndex?: number;
  }>({ isOpen: false, batchIndex: undefined });
  const [selectedDateIndices, setSelectedDateIndices] = useState<Set<number>>(new Set());
  /** Draft input for Hybrid/Online Perpetual day numbers */
  const [perpetualDaysDraft, setPerpetualDaysDraft] = useState<string>('');
  /** Draft input for batch "days" (on-campus/via-zoom: "batchIdx-sbIdx" -> value) */
  const [batchScheduleDateDrafts, setBatchScheduleDateDrafts] = useState<Record<string, string>>({});

  // Initialize schedule only when schedule is truly missing (do not overwrite loaded API data)
  useEffect(() => {
    if (data && data.schedule === undefined) {
      const defaultSchedule = getDefaultScheduleForType(programType);
      onUpdate({ schedule: defaultSchedule });
      onFieldChange?.('schedule');
    }
  }, [programType, data?.schedule, onUpdate, onFieldChange]);


  const updateSchedule = useCallback((updates: Partial<ProgramScheduleV2>) => {
    const newSchedule = { ...scheduleData, ...updates };
    onUpdate({ schedule: newSchedule });
    onFieldChange?.('schedule');
  }, [scheduleData, onUpdate, onFieldChange]);

  const confirmRemoveLegacyHybridOnlineScheduleBatches = useCallback((): boolean => {
    const hasLegacy = Array.isArray(scheduleData.scheduleBatches) && (scheduleData.scheduleBatches?.length ?? 0) > 0;
    if (!hasLegacy) return true;
    return window.confirm(
      'This program still has legacy scheduleBatches rules. Saving a new Hybrid/Online schedule requires removing them (backend rejects ambiguous combinations). Continue?'
    );
  }, [scheduleData.scheduleBatches]);

  const applyHybridOnlineSpecificDatesUpdate = useCallback((dates: string[] | undefined) => {
    if (!confirmRemoveLegacyHybridOnlineScheduleBatches()) return;
    updateSchedule({
      dates,
      // Clear other mode by setting empty array (backend rejects null).
      recurringDaysOfMonth: [],
      // Clear legacy explicitly with empty array (backend rejects null).
      scheduleBatches: [],
      month: undefined,
      year: undefined,
    });
  }, [confirmRemoveLegacyHybridOnlineScheduleBatches, updateSchedule]);

  // Copy handler for copying schedule data from another program type
  const handleCopySchedule = (sourceType: DeliveryModeV2, sourceData: any) => {
    const copiedData: any = {};
    let copiedFields: string[] = [];

    // Copy schedule data
    if (sourceData?.schedule) {
      copiedData.schedule = { ...sourceData.schedule };
      copiedFields.push('schedule');
    }

    if (copiedFields.length > 0) {
      onUpdate(copiedData);
      copiedFields.forEach(field => onFieldChange?.(field));
      toast.success(`Copied ${copiedFields.join(', ')} from ${sourceType}`);
    } else {
      toast.error(`No schedule data found in ${sourceType} program type`);
    }
  };

  // Handle bulk date modal
  const handleBulkDateOpen = (batchIndex?: number) => {
    setBulkDateModal({ isOpen: true, batchIndex });
  };

  const handleBulkDateSave = (dateStrings: string[]) => {
    const { batchIndex } = bulkDateModal;

    // For hybrid/online (add to main dates array)
    if (batchIndex === undefined && (programType === 'hybrid' || programType === 'online')) {
      if (!confirmRemoveLegacyHybridOnlineScheduleBatches()) {
        setBulkDateModal({ isOpen: false, batchIndex: undefined });
        return;
      }

      // Parse dates - try to parse full dates first, then fall back to ordinal parsing
      const parsedDates: string[] = [];
      const currentDate = new Date();
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      
      // Try to infer month/year from existing dates, or use current month/year as fallback
      let inferredMonth = monthNames[currentDate.getMonth()];
      let inferredYear = currentDate.getFullYear();
      
      if (scheduleData.dates && scheduleData.dates.length > 0) {
        // Get the most recent date to infer month/year
        const lastDate = scheduleData.dates[scheduleData.dates.length - 1];
        if (lastDate) {
          const parts = lastDate.split('-');
          if (parts.length === 3) {
            inferredYear = parseInt(parts[0], 10);
            const monthIndex = parseInt(parts[1], 10) - 1;
            if (monthIndex >= 0 && monthIndex < 12) {
              inferredMonth = monthNames[monthIndex];
            }
          }
        }
      }

      // Parse each date string
      for (const dateStr of dateStrings) {
        const trimmed = dateStr.trim();
        if (!trimmed) continue;

        // Try parsing as full date first (MMDDYYYY, MM/DD/YYYY, MM-DD-YYYY, YYYY-MM-DD)
        const fullDateMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
        const mmddyyyyMatch = trimmed.match(/^(\d{2})(\d{2})(\d{4})$/);
        const yyyymmddMatch = trimmed.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);

        if (fullDateMatch) {
          // MM/DD/YYYY or MM-DD-YYYY format
          const month = parseInt(fullDateMatch[1], 10);
          const day = parseInt(fullDateMatch[2], 10);
          const year = parseInt(fullDateMatch[3], 10);
          if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            // Validate date using UTC to avoid timezone issues
            const date = new Date(Date.UTC(year, month - 1, day));
            if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
              // Format using original parsed values (calendar day components)
              parsedDates.push(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
              continue;
            }
          }
        } else if (mmddyyyyMatch) {
          // MMDDYYYY format
          const month = parseInt(mmddyyyyMatch[1], 10);
          const day = parseInt(mmddyyyyMatch[2], 10);
          const year = parseInt(mmddyyyyMatch[3], 10);
          if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            // Validate date using UTC to avoid timezone issues
            const date = new Date(Date.UTC(year, month - 1, day));
            if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
              // Format using original parsed values (calendar day components)
              parsedDates.push(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
              continue;
            }
          }
        } else if (yyyymmddMatch) {
          // YYYY-MM-DD or YYYY/MM/DD format
          const year = parseInt(yyyymmddMatch[1], 10);
          const month = parseInt(yyyymmddMatch[2], 10);
          const day = parseInt(yyyymmddMatch[3], 10);
          if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            // Validate date using UTC to avoid timezone issues
            const date = new Date(Date.UTC(year, month - 1, day));
            if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
              // Format using original parsed values (calendar day components)
              parsedDates.push(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
              continue;
            }
          }
        } else {
          // Try ordinal format (5th, 10th) - use inferred month/year
          const parsed = parseBulkDateInput(trimmed, inferredMonth, inferredYear);
          if (parsed.length > 0) {
            parsedDates.push(...parsed);
          }
        }
      }

      const existingDates = new Set(scheduleData.dates || []);
      const newDates = parsedDates.filter(date => !existingDates.has(date));

      if (newDates.length > 0) {
        updateSchedule({
          dates: [...(scheduleData.dates || []), ...newDates],
          recurringDaysOfMonth: [],
          scheduleBatches: [],
          month: undefined,
          year: undefined,
        });
        toast.success(`Added ${newDates.length} date(s)`);
      } else {
        toast.info('No new dates to add');
      }
    }
    // For on-campus/via-zoom (add to specific batch)
    else if (batchIndex !== undefined && (programType === 'on-campus' || programType === 'via-zoom')) {
      const batches = scheduleData.batches || [];
      if (batchIndex >= batches.length) {
        toast.error('Invalid batch');
        return;
      }

      // For batches, we use current month/year for parsing
      const currentDate = new Date();
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const parsedDates = parseBulkDateInput(
        dateStrings.join('\n'), 
        monthNames[currentDate.getMonth()],
        currentDate.getFullYear()
      );
      
      const batch = batches[batchIndex];
      const existingDates = new Set(batch.dates || []);
      const newDates = parsedDates.filter(date => !existingDates.has(date));
      
      if (newDates.length > 0) {
        const newBatches = [...batches];
        newBatches[batchIndex] = {
          ...batch,
          dates: [...(batch.dates || []), ...newDates]
        };
        updateSchedule({ batches: newBatches });
        toast.success(`Added ${newDates.length} date(s) to batch`);
      } else {
        toast.info('No new dates to add');
      }
    }

    setBulkDateModal({ isOpen: false, batchIndex: undefined });
  };

  // Hybrid/Online Schedule 
  if (programType === 'hybrid' || programType === 'online') {
    const hasLegacySpecific =
      Array.isArray(scheduleData.scheduleBatches) && (scheduleData.scheduleBatches?.length ?? 0) > 0;
    const isPerpetualMode =
      Array.isArray(scheduleData.recurringDaysOfMonth) && (scheduleData.recurringDaysOfMonth?.length ?? 0) > 0;
    const isDailyEnrollment = scheduleData.isDailyEnrollment === true;
    const defaultPerpetualDays = [1];

      return (
      <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Schedule - {programType === 'hybrid' ? 'Hybrid' : 'Online'} Program</CardTitle>
            {activeProgramType && programTypes && (
              <CopyFromProgramType
                currentStep="schedule"
                activeProgramType={activeProgramType}
                programTypes={programTypes}
                onCopy={handleCopySchedule}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Rolling/daily enrollment: no fixed start dates needed — students always start the next calendar day. */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="daily-enrollment-toggle" className="text-base cursor-pointer select-none">
                Daily enrollment (rolling admission)
              </Label>
              <p className="text-sm text-muted-foreground">
                Students can enroll any day. Class start the next calendar day — no start dates to configure.
              </p>
            </div>
            <Switch
              id="daily-enrollment-toggle"
              checked={isDailyEnrollment}
              onCheckedChange={(checked) => {
                if (checked) {
                  if (!confirmRemoveLegacyHybridOnlineScheduleBatches()) return;
                  updateSchedule({
                    isDailyEnrollment: true,
                    dates: [],
                    recurringDaysOfMonth: [],
                    scheduleBatches: [],
                    month: undefined,
                    year: undefined,
                  });
                } else {
                  updateSchedule({ isDailyEnrollment: false });
                }
              }}
            />
          </div>

          {isDailyEnrollment ? (
            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              Daily enrollment is on for this program type. Perpetual/Specific start dates are hidden because they
              aren&apos;t needed — every enrollment starts the next day.
            </div>
          ) : (
          <>
          {/* Admin requirement: two options only (Perpetual vs Specific). */}
           <div className="flex items-center justify-between rounded-lg border p-4">
             <div className="space-y-0.5">
               <Label htmlFor="schedule-mode-toggle" className="text-base cursor-pointer select-none">
                 Schedule mode
               </Label>
               <p className="text-sm text-muted-foreground">
                 Choose how Hybrid/Online start dates are generated.
               </p>
               {hasLegacySpecific && (
                 <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                   <p className="text-sm font-medium text-amber-900">Legacy schedule detected</p>
                   <p className="text-xs text-amber-800">
                     This program has older “Specific (month + day numbers)” rules stored as <code>scheduleBatches</code>.
                     These rules repeat every year and are shown read-only below. New edits should use Perpetual or Specific (calendar dates).
                   </p>
                   <div className="mt-2">
                     <Button
                       type="button"
                       variant="outline"
                       size="sm"
                       onClick={() => {
                         if (!window.confirm('Remove legacy scheduleBatches rules from this program type? This cannot be undone.')) return;
                      updateSchedule({ scheduleBatches: [], month: undefined, year: undefined });
                         toast.success('Legacy schedule rules removed');
                       }}
                     >
                       Clear legacy rules
                     </Button>
                   </div>
                 </div>
               )}
             </div>
             <Switch
               id="schedule-mode-toggle"
               checked={isPerpetualMode}
               onCheckedChange={(checked) => {
                 // checked=true => Perpetual. checked=false => Specific (calendar dates).
                 const hasLegacy = Array.isArray(scheduleData.scheduleBatches) && (scheduleData.scheduleBatches?.length ?? 0) > 0;
                 if (hasLegacy) {
                   if (!window.confirm('Switching modes will remove legacy scheduleBatches rules (they cannot coexist with the new modes). Continue?')) {
                     return;
                   }
                 }

                 updateSchedule(
                   checked
                     ? {
                        scheduleBatches: [],
                         month: undefined,
                         year: undefined,
                        dates: [],
                         recurringDaysOfMonth:
                           (scheduleData.recurringDaysOfMonth?.length ?? 0) > 0
                             ? scheduleData.recurringDaysOfMonth!
                             : defaultPerpetualDays,
                       }
                     : {
                        scheduleBatches: [],
                         month: undefined,
                         year: undefined,
                        recurringDaysOfMonth: [],
                       }
                 );
               }}
             />
           </div>

          <div>
            {isPerpetualMode ? (
              /* Perpetual: same day numbers repeat every month/year */
              <>
                <div className="rounded-lg border p-4 space-y-2">
                  <Label className="text-sm">Perpetual days of month (1–31)</Label>
                  <p className="text-xs text-muted-foreground">
                    These day numbers will generate start dates every month (and every year) until you switch back to Specific dates.
                  </p>
                  <Input
                    type="text"
                    value={perpetualDaysDraft || (scheduleData.recurringDaysOfMonth || []).join(', ')}
                    onChange={(e) => setPerpetualDaysDraft(e.target.value)}
                    onBlur={() => {
                      const parsed = parseDaysOfMonthNumbers(perpetualDaysDraft || (scheduleData.recurringDaysOfMonth || []).join(', '), 31);
                      if (!confirmRemoveLegacyHybridOnlineScheduleBatches()) {
                        setPerpetualDaysDraft('');
                        return;
                      }
                      if (!parsed.length) {
                        toast.error('Enter at least one day of month (1–31).');
                        setPerpetualDaysDraft('');
                        return;
                      }
                      updateSchedule({
                        recurringDaysOfMonth: parsed,
                        dates: [],
                        scheduleBatches: [],
                        month: undefined,
                        year: undefined,
                      });
                      setPerpetualDaysDraft('');
                    }}
                    placeholder="e.g. 5, 15, 25"
                  />
                </div>
              </>
            ) : (
              /* Specific: concrete calendar dates (YYYY-MM-DD) */
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-3">
                    <Label className="mb-0">Start Dates</Label>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="select-all-dates"
                        checked={
                          (scheduleData.dates?.length ?? 0) === 0
                            ? false
                            : selectedDateIndices.size === (scheduleData.dates?.length ?? 0)
                              ? true
                              : selectedDateIndices.size > 0
                                ? 'indeterminate'
                                : false
                        }
                        onCheckedChange={(checked) => {
                          const dates = scheduleData.dates ?? [];
                          if (dates.length === 0) return;
                          if (checked === true) {
                            setSelectedDateIndices(new Set(dates.map((_, i) => i)));
                          } else {
                            setSelectedDateIndices(new Set());
                          }
                        }}
                      />
                      <label htmlFor="select-all-dates" className="text-sm text-muted-foreground cursor-pointer select-none">
                        Select all
                      </label>
                    </div>
                    {selectedDateIndices.size > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const dates = scheduleData.dates ?? [];
                          const newDates = dates.filter((_, i) => !selectedDateIndices.has(i));
                          applyHybridOnlineSpecificDatesUpdate(newDates);
                          setSelectedDateIndices(new Set());
                          toast.success(`Removed ${selectedDateIndices.size} date(s)`);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete selected ({selectedDateIndices.size})
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkDateOpen()}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Bulk Add Dates
                  </Button>
                </div>
                <div className="space-y-2">
                  {(scheduleData.dates || []).map((date, index) => {
                    const dateParts = date ? date.split('-') : null;
                    const displayDate = dateParts && dateParts.length === 3
                      ? new Date(
                          parseInt(dateParts[0], 10),
                          parseInt(dateParts[1], 10) - 1,
                          parseInt(dateParts[2], 10),
                          12, 0, 0
                        )
                      : undefined;
                    const isSelected = selectedDateIndices.has(index);

                    return (
                      <div key={index} className="flex items-center gap-2">
                        <Checkbox
                          id={`date-${index}`}
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            setSelectedDateIndices((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(index);
                              else next.delete(index);
                              return next;
                            });
                          }}
                        />
                        <DatePicker
                          date={displayDate}
                          setDate={(selectedDate) => {
                            if (selectedDate) {
                              const year = selectedDate.getFullYear();
                              const month = selectedDate.getMonth();
                              const day = selectedDate.getDate();
                              const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                              const newDates = [...(scheduleData.dates || [])];
                              newDates[index] = dateString;
                              applyHybridOnlineSpecificDatesUpdate(newDates);
                            } else {
                              const newDates = [...(scheduleData.dates || [])];
                              newDates[index] = '';
                              applyHybridOnlineSpecificDatesUpdate(newDates);
                            }
                          }}
                          placeholder="Select date"
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            const newDates = (scheduleData.dates || []).filter((_, i) => i !== index);
                            applyHybridOnlineSpecificDatesUpdate(newDates);
                            setSelectedDateIndices((prev) => {
                              const next = new Set<number>();
                              prev.forEach((i) => {
                                if (i === index) return;
                                next.add(i > index ? i - 1 : i);
                              });
                              return next;
                            });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                  <Button
                    variant="outline"
                    onClick={() => {
                      const newDates = [...(scheduleData.dates || []), ''];
                      applyHybridOnlineSpecificDatesUpdate(newDates);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Date
                  </Button>
                </div>
                {/* Legacy scheduleBatches shown read-only, if present */}
                {Array.isArray(scheduleData.scheduleBatches) && (scheduleData.scheduleBatches?.length ?? 0) > 0 && (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm font-medium text-amber-900 mb-1">Legacy specific rules (read-only)</p>
                    <p className="text-xs text-amber-800 mb-2">
                      Stored as month + day numbers (repeats yearly). These are not edited in the new two-mode UI.
                    </p>
                    <div className="text-sm font-medium text-amber-950">
                      {scheduleBatchesSummary(scheduleData.scheduleBatches)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          </>
          )}
        </CardContent>
      </Card>

      {/* Bulk Date Modal */}
      <BulkDateModal
        isOpen={bulkDateModal.isOpen}
        onClose={() => setBulkDateModal({ isOpen: false, batchIndex: undefined })}
        onSave={handleBulkDateSave}
        month={scheduleData.month || 'January'}
        year={scheduleData.year || new Date().getFullYear()}
      />
    </>
    );
  }

  // On-campus/Via-zoom Schedule (flat batches array)
  if (programType === 'on-campus' || programType === 'via-zoom') {
    return (
      <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Schedule - {programType === 'on-campus' ? 'On-Campus' : 'Via-Zoom'} Program</CardTitle>
           
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(scheduleData.batches || []).map((batch, batchIndex) => (
            <Card key={batchIndex} className="border-2">
              <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Batch {batchIndex + 1}</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newBatches = (scheduleData.batches || []).filter((_, i) => i !== batchIndex);
                      updateSchedule({ batches: newBatches });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                          <div>
                  <Label>Batch Name</Label>
                  <Input
                    value={batch.batch || ''}
                    onChange={(e) => {
                      const newBatches = [...(scheduleData.batches || [])];
                      newBatches[batchIndex] = { ...batch, batch: e.target.value };
                      updateSchedule({ batches: newBatches });
                    }}
                    placeholder="e.g., Morning, Evening"
                            />
                          </div>
                          
                <div>
                  <Label>Time Range</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={batch.time?.split('-')[0]?.trim() || ''}
                      onChange={(e) => {
                        const newBatches = [...(scheduleData.batches || [])];
                        const endTime = batch.time?.split('-')[1]?.trim() || '';
                        newBatches[batchIndex] = { ...batch, time: endTime ? `${e.target.value}-${endTime}` : e.target.value };
                        updateSchedule({ batches: newBatches });
                      }}
                      placeholder="Start time"
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="text"
                      value={batch.time?.split('-')[1]?.trim() || ''}
                      onChange={(e) => {
                        const newBatches = [...(scheduleData.batches || [])];
                        const startTime = batch.time?.split('-')[0]?.trim() || '';
                        newBatches[batchIndex] = { ...batch, time: startTime ? `${startTime}-${e.target.value}` : e.target.value };
                        updateSchedule({ batches: newBatches });
                      }}
                      placeholder="End time"
                    />
                  </div>
                </div>
                        
                <div>
                  <Label>Days of Week Range</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={batch.daysOfWeek?.[0] || ''}
                      onValueChange={(value) => {
                        const newBatches = [...(scheduleData.batches || [])];
                        const endDay = batch.daysOfWeek?.[1] || '';
                        newBatches[batchIndex] = { ...batch, daysOfWeek: endDay ? [value, endDay] : [value] };
                        updateSchedule({ batches: newBatches });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Start day" />
                      </SelectTrigger>
                      <SelectContent>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-muted-foreground">-</span>
                    <Select
                      value={batch.daysOfWeek?.[1] || ''}
                      onValueChange={(value) => {
                        const newBatches = [...(scheduleData.batches || [])];
                        const startDay = batch.daysOfWeek?.[0] || '';
                        newBatches[batchIndex] = { ...batch, daysOfWeek: startDay ? [startDay, value] : [value] };
                        updateSchedule({ batches: newBatches });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="End day" />
                      </SelectTrigger>
                      <SelectContent>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {batch.daysOfWeek && batch.daysOfWeek.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Range: {batch.daysOfWeek.filter(d => d).join(' - ')}
                    </p>
                  )}
                </div>
                          
                          <div>
                  {/* Batch: toggle Specific dates (off) vs Start date batches – month + dates (on) */}
                  {(() => {
                    const batchScheduleBatchesMode = Array.isArray(batch.scheduleBatches);
                    return (
                  <div className="flex items-center justify-between rounded-lg border p-3 mb-3">
                    <div className="space-y-0.5">
                      <Label htmlFor={`batch-schedule-batches-${batchIndex}`} className="text-sm">
                        {batchScheduleBatchesMode ? 'Start date batches (month + dates)' : 'Specific dates'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {batchScheduleBatchesMode
                          ? 'Add rows: month + days of that month. Repeats every year.'
                          : 'Concrete calendar dates for this batch.'}
                      </p>
                    </div>
                    <Switch
                      id={`batch-schedule-batches-${batchIndex}`}
                      checked={batchScheduleBatchesMode}
                      onCheckedChange={(checked) => {
                        const newBatches = [...(scheduleData.batches || [])];
                        if (checked) {
                          newBatches[batchIndex] = {
                            ...batch,
                            scheduleBatches: batch.scheduleBatches ?? [],
                          };
                        } else {
                          const { scheduleBatches: _sb, recurringDaysOfMonth: _rd, ...rest } = batch as any;
                          newBatches[batchIndex] = { ...rest };
                        }
                        updateSchedule({ batches: newBatches });
                      }}
                    />
                  </div>
                    );
                  })()}

                  {Array.isArray(batch.scheduleBatches) ? (
                    /* Start date batches for this batch: month + dates per row */
                    <>
                      <div className="space-y-3">
                        {(batch.scheduleBatches || []).map((entry, sbIdx) => {
                          const maxDay = getMaxDaysInMonth(entry.month || 'January');
                          const datesStr = (entry.dates || []).map((d: any) => String(d)).join(', ');
                          return (
                            <div key={sbIdx} className="flex flex-wrap items-end gap-2 rounded-lg border p-3">
                              <div className="min-w-[120px]">
                                <Label className="text-xs text-muted-foreground">Month</Label>
                                <Select
                                  value={entry.month || 'January'}
                                  onValueChange={(v) => {
                                    const newBatches = [...(scheduleData.batches || [])];
                                    const sb = [...(batch.scheduleBatches || [])];
                                    sb[sbIdx] = { ...entry, month: v, dates: parseDaysOfMonthStrings(datesStr, getMaxDaysInMonth(v)) };
                                    newBatches[batchIndex] = { ...batch, scheduleBatches: sb };
                                    updateSchedule({ batches: newBatches });
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {MONTH_NAMES.map((m) => (
                                      <SelectItem key={m} value={m}>{m}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex-1 min-w-[160px]">
                                <Label className="text-xs text-muted-foreground">Days (1–{maxDay})</Label>
                                <Input
                                  type="text"
                                  value={batchScheduleDateDrafts[`${batchIndex}-${sbIdx}`] ?? datesStr}
                                  onChange={(e) => setBatchScheduleDateDrafts((prev) => ({ ...prev, [`${batchIndex}-${sbIdx}`]: e.target.value }))}
                                  onBlur={() => {
                                    const raw = batchScheduleDateDrafts[`${batchIndex}-${sbIdx}`] ?? datesStr;
                                    const parsed = parseDaysOfMonthStrings(raw, maxDay);
                                    const newBatches = [...(scheduleData.batches || [])];
                                    const sb = [...(batch.scheduleBatches || [])];
                                    sb[sbIdx] = { ...entry, dates: parsed };
                                    newBatches[batchIndex] = { ...batch, scheduleBatches: sb };
                                    updateSchedule({ batches: newBatches });
                                    setBatchScheduleDateDrafts((prev) => {
                                      const next = { ...prev };
                                      delete next[`${batchIndex}-${sbIdx}`];
                                      return next;
                                    });
                                  }}
                                  placeholder="e.g. 5, 15, 25"
                                />
                              </div>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  const newBatches = [...(scheduleData.batches || [])];
                                  const sb = (batch.scheduleBatches || []).filter((_, i) => i !== sbIdx);
                                  newBatches[batchIndex] = { ...batch, scheduleBatches: sb.length ? sb : [] };
                                  updateSchedule({ batches: newBatches });
                                  setBatchScheduleDateDrafts((prev) => {
                                    const next = { ...prev };
                                    delete next[`${batchIndex}-${sbIdx}`];
                                    return next;
                                  });
                                }}
                                aria-label="Remove row"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          const newBatches = [...(scheduleData.batches || [])];
                          const sb = [...(batch.scheduleBatches || []), { month: 'January', dates: [] }];
                          newBatches[batchIndex] = { ...batch, scheduleBatches: sb };
                          updateSchedule({ batches: newBatches });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add batch
                      </Button>
                      {batch.scheduleBatches && batch.scheduleBatches.length > 0 && (
                        <div className="mt-3 rounded-md bg-muted/50 px-3 py-2">
                          <p className="text-xs text-muted-foreground mb-1">Summary</p>
                          <p className="text-sm font-medium">{scheduleBatchesSummary(batch.scheduleBatches)}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Specific dates for batch */
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Dates</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBulkDateOpen(batchIndex)}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Bulk Add
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {(batch.dates || []).map((date, dateIndex) => (
                          <div key={dateIndex} className="flex gap-2">
                            <Input
                              type="date"
                              value={date}
                              onChange={(e) => {
                                const newBatches = [...(scheduleData.batches || [])];
                                const newDates = [...(batch.dates || [])];
                                newDates[dateIndex] = e.target.value;
                                newBatches[batchIndex] = { ...batch, dates: newDates };
                                updateSchedule({ batches: newBatches });
                              }}
                            />
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                const newBatches = [...(scheduleData.batches || [])];
                                const newDates = (batch.dates || []).filter((_, i) => i !== dateIndex);
                                newBatches[batchIndex] = { ...batch, dates: newDates.length ? newDates : undefined };
                                updateSchedule({ batches: newBatches });
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newBatches = [...(scheduleData.batches || [])];
                            const newDates = [...(batch.dates || []), ''];
                            newBatches[batchIndex] = { ...batch, dates: newDates };
                            updateSchedule({ batches: newBatches });
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Date
                        </Button>
                      </div>
                    </>
                  )}
                          </div>
              </CardContent>
            </Card>
          ))}

          <Button
            variant="outline"
            onClick={() => {
              const newBatches = [...(scheduleData.batches || []), { batch: '', dates: [], daysOfWeek: [], time: '' }];
              updateSchedule({ batches: newBatches });
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Batch
          </Button>
        </CardContent>
      </Card>

      {/* Bulk Date Modal */}
      <BulkDateModal
        isOpen={bulkDateModal.isOpen}
        onClose={() => setBulkDateModal({ isOpen: false, batchIndex: undefined })}
        onSave={handleBulkDateSave}
        month={['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][new Date().getMonth()]}
        year={new Date().getFullYear()}
      />
    </>
    );
  }

  return (
    <Card>
      <CardContent className="py-8">
        <p className="text-center text-muted-foreground">Schedule configuration for this program type is not available.</p>
      </CardContent>
    </Card>
  );
};
